import type { MoneyPayload } from '../../api/types';

/**
 * Money at the form boundary, and money on the page.
 *
 * The API sends and receives an exact decimal string plus a currency, never a number: a price that
 * passes through a JavaScript number on its way to the ledger is a price that eventually rounds.
 * These functions are the only places the dashboard crosses that boundary.
 */

/** Form value → the API's money shape, or undefined when the field was left blank. */
export function readMoney(value: FormDataEntryValue | null): MoneyPayload | undefined {
  const raw = String(value ?? '').trim();
  if (raw === '') {
    return undefined;
  }
  return { amount: Number(raw).toFixed(2), currency: 'EGP' };
}

/**
 * Grouping is applied to the integer part by hand rather than by `Intl.NumberFormat`, because the
 * amount arrives as an exact decimal *string* and putting it through a number to format it is the
 * one thing the boundary above exists to prevent. A receipt total of 12600.00 is grouped, never
 * parsed.
 *
 * Western digits even in Arabic (ASM-004): clinic staff read prices in 12,600 rather than ١٢٬٦٠٠,
 * which is what the partner clinics write on paper.
 */
const GROUP = /\B(?=(\d{3})+(?!\d))/g;

/**
 * The API's money shape → something to put in a table cell.
 *
 * Two things changed here in the 2026-08 craft pass, and both were legibility rather than taste.
 *
 * The amount is **grouped**: it used to render as `12600.00 EGP`, and a column of ungrouped
 * five-figure amounts cannot be compared by eye — the reader counts digits. It is now `12,600.00`.
 *
 * The currency leads and is **shortened when every amount on a screen shares it**, which in this
 * product is every screen: the clinic bills in one currency (RULE-001), so repeating `EGP` at the
 * end of forty cells says nothing forty times while pushing the digits out of alignment. The
 * currency is still printed — silently dropping it from a financial figure is not an option — but
 * it goes in front, where it reads as a unit rather than as part of the number, and where a column
 * of amounts still aligns on its decimal point.
 */
export function money(value: MoneyPayload | undefined | null): string | null {
  if (!value?.amount) {
    return null;
  }
  const [whole = '0', fraction] = String(value.amount).split('.');
  const negative = whole.startsWith('-');
  const digits = negative ? whole.slice(1) : whole;
  const grouped = digits.replace(GROUP, ',');
  const amount = `${negative ? '-' : ''}${grouped}${fraction ? `.${fraction}` : ''}`;
  return `${value.currency ?? 'EGP'} ${amount}`;
}

/**
 * The same figure without its currency, for a column whose heading already states the unit.
 *
 * Used by the measure strip and by chart labels, where the currency appears once above the whole
 * reading and repeating it inside every value is noise.
 */
export function amount(value: MoneyPayload | undefined | null): string | null {
  const formatted = money(value);
  if (!formatted) {
    return null;
  }
  return formatted.slice(formatted.indexOf(' ') + 1);
}

/** The numeric value of a money payload, for comparisons and chart geometry only — never for display. */
export function magnitude(value: MoneyPayload | undefined | null): number {
  const parsed = Number(value?.amount ?? Number.NaN);
  return Number.isFinite(parsed) ? parsed : 0;
}
